/**
 * Sound utility for Odin Sight
 * Provides simple audio feedback for user interactions
 */

import { useSettingsStore } from "../store/settingsStore.jsx";

// Sound configurations
const SOUNDS = {
  coin: {
    // A pleasant "ding" sound for successful operations
    frequency: 800,
    duration: 200,
    type: "sine",
  },
  bump: {
    // A lower "bump" sound for errors or warnings
    frequency: 300,
    duration: 150,
    type: "sawtooth",
  },
  ping: {
    // A quick "ping" sound for confirmations
    frequency: 600,
    duration: 100,
    type: "square",
  },
};

/**
 * Play a sound using Web Audio API
 * @param {string} soundName - Name of the sound to play ('coin', 'bump', 'ping')
 * @param {number} volume - Volume level (0.0 to 1.0, default: 0.3)
 * @param {object} context - Additional context for logging
 */
export function playSound(soundName, volume = 0.3) {
  try {
    // Check if routing sounds are enabled in settings
    const { routingSounds } = useSettingsStore.getState();
    if (!routingSounds) {
      // Sounds are disabled, just return silently
      return;
    }

    const sound = SOUNDS[soundName];
    if (!sound) {
      console.warn(`Sound "${soundName}" not found`);
      return;
    }

    // Check if Web Audio API is supported
    if (!window.AudioContext && !window.webkitAudioContext) {
      console.warn("Web Audio API not supported");
      return;
    }

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContext();

    // Create oscillator for tone generation
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    // Configure oscillator
    oscillator.frequency.setValueAtTime(
      sound.frequency,
      audioContext.currentTime,
    );
    oscillator.type = sound.type;

    // Configure volume
    gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + sound.duration / 1000,
    );

    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Play sound
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + sound.duration / 1000);
  } catch (error) {
    console.warn("Error playing sound:", error);
  }
}

/**
 * Check if audio is available and user hasn't disabled it
 */
export function isAudioEnabled() {
  try {
    return !!(window.AudioContext || window.webkitAudioContext);
  } catch {
    return false;
  }
}

/**
 * Play multiple sounds in sequence
 * @param {string[]} soundNames - Array of sound names to play
 * @param {number} delay - Delay between sounds in ms
 */
export function playSoundSequence(soundNames, delay = 100) {
  soundNames.forEach((soundName, index) => {
    setTimeout(() => playSound(soundName), index * delay);
  });
}

export default playSound;
